import { useState, useEffect } from 'react';
import 'react-quill/dist/quill.snow.css';
import { Controller, useFormContext } from 'react-hook-form';
import type { ControllerRenderProps } from 'react-hook-form';
import { PendingUpload } from 'alurkerja-ui';
import SwitchButton from './SwitchButton';
import { MarkdownInput } from '@/components/ui/MarkdownInput';
import RichTextEditor from '@/components/ui/RichTextEditor';

export interface FormField {
  name: string;
  label: string;
  type?:
    | 'text'
    | 'number'
    | 'date'
    | 'password'
    | 'select'
    | 'textarea'
    | 'switch'
    | 'upload'
    | 'richtext'
    | 'markdown';
  uploadType?: 'image' | 'file';
  options?: { label: string; value: string }[];
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  labelRight?: React.ReactNode;
  onFileChange?: (file: File | null) => void;
  onDownload?: (file: unknown) => void;
}

interface FormBuilderProps {
  fields: FormField[];
  initialPublished?: boolean;
  onPublishChange?: (value: boolean) => void;
  hidePublish?: boolean;
  disabled?: boolean;
  dark?: boolean;
}

interface FieldControlProps {
  field: FormField;
  f: ControllerRenderProps;
  disabled: boolean;
  dark: boolean;
}

function FieldControl({ field, f, disabled, dark }: Readonly<FieldControlProps>) {
  switch (field.type) {
    case 'date':
      return (
        <input
          {...f}
          type="date"
          className={`text-sm w-full px-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed ${
            dark
              ? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-[#0D0D0D] text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800'
              : 'border-gray-300 disabled:bg-gray-100'
          }`}
          placeholder={field.placeholder || 'Pilih tanggal'}
          disabled={disabled}
        />
      );

    case 'select':
      return (
        <select
          aria-label={field.label}
          className={`text-sm w-full h-[38px] px-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none m-0 leading-tight disabled:cursor-not-allowed ${
            dark
              ? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-[#0D0D0D] text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800'
              : 'border-gray-300 disabled:bg-gray-100'
          }`}
          value={f.value ?? ''}
          onChange={e => f.onChange(e.target.value)}
          disabled={disabled}
        >
          {field.placeholder && (
            <option value="" disabled hidden>
              {field.placeholder}
            </option>
          )}
          {(field.options || []).map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );

    case 'textarea':
      return (
        <textarea
          {...f}
          rows={4}
          className={`text-sm w-full px-3 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed ${
            dark
              ? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-[#0D0D0D] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 disabled:bg-gray-100 dark:disabled:bg-gray-800'
              : 'border-gray-300 disabled:bg-gray-100'
          }`}
          placeholder={field.placeholder}
          disabled={disabled || field.disabled}
        />
      );

    case 'switch':
      return (
        <div
          className={`flex items-center justify-between border rounded-xl p-2 ${
            dark ? 'border-gray-200 dark:border-gray-600' : 'border-gray-200'
          }`}
        >
          <span className="text-sm">{field.label}</span>
          <SwitchButton checked={f.value} onChange={f.onChange} disabled={disabled} />
        </div>
      );

    case 'upload':
      return <UploadField field={field} f={f} disabled={disabled} />;

    case 'richtext':
      return <RichTextEditor value={f.value || ''} onChange={f.onChange} disabled={disabled} />;

    case 'markdown':
      return (
        <MarkdownInput
          value={f.value || ''}
          onChange={f.onChange}
          className="w-full mt-1"
          placeholder={field.placeholder || 'Mendukung *tebal*, _miring_, ~~coret~~, `kode`'}
          disabled={disabled}
          dark={dark}
        />
      );

    default:
      return (
        <input
          {...f}
          type={field.type || 'text'}
          className={`text-sm w-full px-3 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed ${
            dark
              ? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-[#0D0D0D] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 disabled:bg-gray-100 dark:disabled:bg-gray-800'
              : 'border-gray-300 disabled:bg-gray-100'
          }`}
          placeholder={field.placeholder || field.label}
          disabled={disabled}
        />
      );
  }
}

function UploadField({ field, f, disabled }: Readonly<Omit<FieldControlProps, 'dark'>>) {
  const isImageUpload = field.uploadType === 'image';
  type UploadItem = { file?: File };
  return (
    <div className={disabled ? 'pointer-events-none opacity-60' : ''}>
      <PendingUpload
        asFile
        multiple={false}
        value={f.value ? [f.value as File] : []}
        onChange={(files: unknown[]) => {
          const fileObj: unknown = (files as unknown[])[0];
          let file: File | null = null;

          if (fileObj instanceof File) {
            file = fileObj;
          } else if (fileObj && (fileObj as UploadItem).file instanceof File) {
            file = (fileObj as UploadItem).file as File;
          }

          f.onChange(file);
          field.onFileChange?.(file);
        }}
        onDownload={field.onDownload}
        allowedExtension={field.uploadType === 'image' ? ['jpg', 'jpeg', 'png', 'webp'] : ['pdf', 'doc', 'docx']}
      />

      {!isImageUpload && (
        <small className="text-gray-500 mt-1">
          Mendukung: <strong>PDF, DOC, DOCX</strong> saja.
        </small>
      )}
      {isImageUpload && (
        <small className="text-gray-500 mt-1">
          Mendukung: <strong>JPG, JPEG, PNG, atau WEBP</strong> saja.
        </small>
      )}
    </div>
  );
}

function buildFieldRules(field: FormField) {
  if (field.type === 'richtext') {
    return {
      validate: (v: string) => {
        const cleaned = v.replace(/<(.|\n)*?>/g, '').trim();
        return cleaned.length > 0 || `${field.label} wajib diisi`;
      },
    };
  }
  if (field.required) {
    return { required: `${field.label} wajib diisi` };
  }
  return undefined;
}

export default function FormBuilder({
  fields,
  initialPublished = false,
  onPublishChange,
  hidePublish,
  disabled = false,
  dark = false,
}: Readonly<FormBuilderProps>) {
  const { control } = useFormContext();
  const [isPublished, setIsPublished] = useState(initialPublished);

  useEffect(() => {
    setIsPublished(initialPublished);
  }, [initialPublished]);

  return (
    <div className="space-y-4 mx-5 md:mx-1">
      {fields.map(field => (
        <div key={field.name}>
          <div className="flex justify-between items-center mb-1">
            <label className={`block text-sm font-medium ${dark ? 'dark:text-gray-300' : ''}`}>
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
            {field.labelRight && <div>{field.labelRight}</div>}
          </div>

          <Controller
            name={field.name}
            control={control}
            rules={buildFieldRules(field)}
            render={({ field: f, fieldState: { error } }) => (
              <div>
                <FieldControl field={field} f={f} disabled={disabled} dark={dark} />
                {error && <p className="text-xs text-red-500 mt-1">{error.message}</p>}
              </div>
            )}
          />
        </div>
      ))}

      {!hidePublish && (
        <div className="flex gap-1 items-center">
          <SwitchButton
            checked={isPublished}
            onChange={value => {
              setIsPublished(value);
              onPublishChange?.(value);
            }}
            disabled={disabled}
          />
          <span className="text-sm font-semibold">Publikasikan</span>
        </div>
      )}
    </div>
  );
}
